import React, { useState, useRef, useEffect, memo } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  value: string;
  onChange: (v: string) => void;
  className?: string;
}

const ranges = ['1min','5min','10min','15min','30min','1h','4h'];

export const TimeOfDayDropdown: React.FC<Props> = ({ value, onChange, className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('pointerdown', handle);

    // Prevent document-level capture-phase pointerdown handlers from seeing
    // events that originate inside this dropdown. This ensures capture listeners
    // (e.g. global drag handlers added with `capture: true`) don't close
    // the menu before we can handle selection.
    const root = ref.current;
    const stopCapture = (ev: Event) => {
      ev.stopPropagation();
    };
    if (root) root.addEventListener('pointerdown', stopCapture, { capture: true });

    return () => {
      document.removeEventListener('pointerdown', handle);
      if (root) root.removeEventListener('pointerdown', stopCapture, { capture: true } as any);
    };
  }, []);

  return (
    <div className={`relative z-[2147483647] ${className}`} ref={ref}>
      <button
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); setIsOpen(o => !o); }}
        className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors
          ${isOpen
            ? 'bg-zinc-50 dark:bg-zinc-600 border-zinc-300 dark:border-zinc-600 text-zinc-800 dark:text-zinc-100'
            : 'bg-zinc-100 dark:bg-zinc-800 border-zinc-300 dark:border-zinc-600 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-600'
        }
        `}
      >
        <span className="truncate">{value}</span>
        {isOpen ? (
          <ChevronUp className="w-3 h-3 text-zinc-500 dark:text-zinc-400" />
        ) : (
          <ChevronDown className="w-3 h-3 text-zinc-500 dark:text-zinc-400" />
        )}
      </button>

      {isOpen && (
        <div
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className="absolute top-full right-0 mt-2 w-28 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-lg shadow-lg overflow-hidden z-[2147483647] flex flex-col py-1 animate-in fade-in zoom-in-95 duration-100"
          style={{ fontSize: '14px' }}
        >
          {ranges.map(r => (
            <button
              key={r}
              onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); onChange(r); setIsOpen(false); }}
              onClick={() => { onChange(r); setIsOpen(false); }}
              className={`px-3 py-2 text-left transition-colors ${value === r ? 'text-zinc-900 dark:text-zinc-100 font-medium bg-zinc-100 dark:bg-zinc-800' : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800'}`}
            >
              <span className="truncate">{r}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(TimeOfDayDropdown);
